import { Label, Select } from "@tellescope/design-system";
import { useStateWithSetAction } from "@tellescope/ui";
import { cn } from "@tellescope/utils";
import { FormResponseValue } from "@tellescope/types-models";
import { TellescopeFormFieldBaseProps } from "../types";
import {
  resolveFieldOptionDetails,
  resolveVisibleChoices,
  translateFieldOptionLabels,
  type FormTranslationMap,
} from "../utils";
import type { NextFieldLogicOptions } from "../utils/types";
import { useTranslation } from "react-i18next";
import { FieldDescription } from "../FieldDescription";
import { useMemo } from "react";

export interface DropdownFieldProps extends TellescopeFormFieldBaseProps<"Dropdown"> {
  formResponses?: FormResponseValue[];
  logicOptions?: NextFieldLogicOptions;
  translations?: FormTranslationMap;
}

export const DropdownField = ({
  title,
  description,
  htmlDescription,
  placeholder,
  value = [],
  onChange,
  className,
  options,
  onBlur,
  error,
  required,
  formResponses,
  logicOptions,
  autoFocus,
  disabled,
  translations,
}: DropdownFieldProps) => {
  const { t } = useTranslation("forms");
  const [open, setOpen] = useStateWithSetAction(false, (isOpen) => {
    if (!isOpen) {
      onBlur?.();
    }
  });

  const allowOther = !!options?.other;
  const optionDetails = useMemo(
    () =>
      resolveVisibleChoices({
        optionDetails: resolveFieldOptionDetails(options),
        responses: formResponses ?? [],
        logicOptions,
      }),
    [options, formResponses, logicOptions]
  );

  const resolvedPlaceholder = placeholder ?? t("Select item...");

  // `value` stays the authored English — it is what gets stored and what branching and scoring
  // compare against. Only `label`/`description` are translated. A free-text Other answer is the
  // enduser's own typing and is never translated.
  const selectableOptions = useMemo(
    () =>
      translateFieldOptionLabels(optionDetails, translations).map((option) => ({
        value: option.option,
        label: option.label,
        description: option.translatedDescription,
      })),
    [optionDetails, translations]
  );

  // A selected value that is not one of the choices is a custom answer, so it needs its own
  // entry to stay visible and removable in the list. Matched against every authored choice, not
  // just the visible ones, so a choice hidden by conditional logic is not shown as a custom answer.
  const knownOptionValues = useMemo(() => resolveFieldOptionDetails(options).map((o) => o.option), [options]);
  const comboboxOptions = useMemo(
    () =>
      value.reduce(
        (acc, v) => (knownOptionValues.includes(v) ? acc : [...acc, { value: v, label: v, description: undefined }]),
        selectableOptions
      ),
    [selectableOptions, value, knownOptionValues]
  );

  const creator = allowOther
    ? { label: (inputValue: string) => t('Add "{{value}}"', { value: inputValue }), commitOnClose: true }
    : undefined;

  return (
    <div className="space-y-1 w-full">
      <Label required={required}>{title || resolvedPlaceholder}</Label>
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      {options?.radio ? (
        <Select
          clearable
          value={value[0]}
          onValueChange={(selected) => onChange(selected ? [selected] : [])}
          open={open}
          onOpenChange={setOpen}
          disabled={disabled}
          creator={creator}
        >
          <Select.Trigger autoFocus={autoFocus} aria-invalid={!!error} className={cn("w-full", className)}>
            <Select.Value placeholder={resolvedPlaceholder} />
          </Select.Trigger>
          <Select.Content searchPlaceholder={t("Search...")} emptyText={t("No results found")}>
            {comboboxOptions.map((option) => (
              <Select.Item
                key={option.value}
                value={option.value}
                label={option.label}
                textValue={option.label}
                keywords={[option.value]}
              >
                <div className="flex min-w-0 flex-col">
                  <span>{option.label}</span>
                  {!!option.description && <span className="text-xs text-muted-foreground">{option.description}</span>}
                </div>
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      ) : (
        <Select
          multiple
          value={value}
          onValueChange={onChange}
          open={open}
          onOpenChange={setOpen}
          disabled={disabled}
          creator={creator}
        >
          <Select.Trigger autoFocus={autoFocus} aria-invalid={!!error} className={cn("w-full", className)}>
            <Select.Value placeholder={resolvedPlaceholder} />
          </Select.Trigger>
          <Select.Content searchable searchPlaceholder={t("Search...")} emptyText={t("No results found")}>
            {comboboxOptions.map((option) => (
              <Select.Item key={option.value} value={option.value} keywords={[option.value]}>
                {`${option.label} ${option.description ?? ""}`.trim()}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      )}

      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
